import type { FC } from 'react'
import { useEffect, useState, useRef } from 'react'
import PropTypes from 'prop-types'
import {
  Avatar,
  Badge,
  Box,
  Button,
  IconButton,
  Divider,
  Typography,
  CardContent,
  ListItem,
  ListItemAvatar,
  Paper,
  List,
  Popper,
  TextField,
  ListItemText,
  ListItemSecondaryAction,
  Tooltip
} from '@material-ui/core'
import { useSelector, useDispatch } from 'react-redux'
import { useSnackbar } from 'notistack'
import { Device } from '@twilio/voice-sdk'
import CallEndIcon from '@material-ui/icons/CallEnd'
import AddIcon from '@material-ui/icons/Add'
import BorderColorIcon from '@material-ui/icons/BorderColor'
import PhonePausedIcon from '@material-ui/icons/PhonePaused'
import DialpadIcon from '@material-ui/icons/Dialpad'
import CallIcon from '@material-ui/icons/Call'
import PhoneForwardedIcon from '@material-ui/icons/PhoneForwarded'
import PersonAddIcon from '@material-ui/icons/PersonAdd'
import MicOffIcon from '@material-ui/icons/MicOff'
import MicIcon from '@material-ui/icons/Mic'
import VolumeOffIcon from '@material-ui/icons/VolumeOff'
import VolumeUpIcon from '@material-ui/icons/VolumeUp'
import CloseIcon from '@material-ui/icons/Close'
import Mos5 from '@material-ui/icons/SignalCellular4Bar'
import Mos4 from '@material-ui/icons/NetworkCell'
import Mos3 from '@material-ui/icons/SignalCellularConnectedNoInternet2Bar'
import Mos2 from '@material-ui/icons/SignalCellularConnectedNoInternet1Bar'
import Dialpad from './Dialpad'
import Notes from './Notes'
import {
  formatNumber,
  checkValidNumber,
  userIsParticipant,
  sendAlert,
  getMyDeviceName,
  getAnyMyDeviceParticipantSid
} from '../../../utils/common'
import { filterContacts, filterUsers } from '../../../utils/contacts'
import Scrollbar from '../../Scrollbar'
import useAuth from '../../../hooks/useAuth'
import {
  conferenceControl,
  getDeviceTypeFromLabel
} from '../../../utils/twilio'
import wait from '../../../utils/wait'
import { RootState } from '../../../store'
import { setSelectedCall } from '../../../slices/phone'
import ravenImage from '../../../assets/images/raven.png'

export const getMosIcon = (mos: number | null) => {
  if (mos) {
    if (mos > 4) {
      return (
        <Tooltip title="Good Network">
          <Mos5 />
        </Tooltip>
      )
    }
    if (mos > 3) {
      return (
        <Tooltip title="Degraded Network">
          <Mos4 />
        </Tooltip>
      )
    }
    if (mos > 2) {
      return (
        <Tooltip title="Poor Network">
          <Mos3 color="error" />
        </Tooltip>
      )
    }
    return (
      <Tooltip title="Network Issues">
        <Mos2 color="error" />
      </Tooltip>
    )
  }
  return null
}

interface SelectedCallProps {
  call: any
  contacts: any
  device: Device
  users: any
  inputDevice: string
  removeIncomingCall: Function
}

const SelectedCall: FC<SelectedCallProps> = (props) => {
  const dispatch = useDispatch()
  const {
    call,
    contacts,
    device,
    users,
    inputDevice,
    removeIncomingCall,
    ...other
  } = props

  if (!call) {
    dispatch(setSelectedCall(''))
    return null
  }

  const { participants, conferenceSid, liveParticipants, name } = call
  const { user } = useAuth()
  const { enqueueSnackbar } = useSnackbar()
  const { auth0Token, accountId, australisId } = user
  const liveNotes = useSelector((state: RootState) => state.phone.liveNotes)
  const conferences = useSelector((state: RootState) => state.phone.conferences)
  const [dialpadOpen, setDialpadOpen] = useState(true)
  const [notesOpen, setNotesOpen] = useState(false)
  const [addOpen, setAddOpen] = useState(false)
  const [addNumber, setAddNumber] = useState('')
  const [dialing, setDialing] = useState(false)
  const [contactNumberMap, setContactNumberMap] = useState(false)
  const [mos, setMos] = useState(null)
  const addInputRef = useRef(null)
  const newInputRef = useRef(null)

  let degradedCount = 0
  let alertSent = false

  const onOtherDevices = liveParticipants
    ? !!getAnyMyDeviceParticipantSid(
        liveParticipants,
        australisId,
        participants
      )
    : ''

  const createContactNumberMap = (c: any) => {
    const cmap: any = {}
    for (const contact of c) {
      for (const number of contact.contactDetails.phoneNumbers) {
        cmap[formatNumber(number.number)] = { ...contact, number }
      }
    }
    setContactNumberMap(cmap)
  }

  useEffect(() => {
    // @ts-ignore
    if (device && device._activeCall && call.conf_status === 'inProgress') {
      // @ts-ignore
      device._activeCall.on('disconnect', () => {})
      // @ts-ignore
      device._activeCall.on('sample', (sample: any) => {
        if (sample?.mos < 4 || sample?.jitter > 30) {
          degradedCount++
          if (degradedCount > 10 && !alertSent) {
            sendAlert(
              auth0Token,
              `:rotating_light: Realtime degraded call detected :rotating_light: \n *Date detected:* ${sample?.timestamp} \n *MOS:* ${sample?.mos} \n *Jitter:* ${sample?.jitter} \n *User's name:* ${user.name} \n *User's email:* ${user.email} \n *Australis ID:* ${user.australisId} \n *Account ID:* ${user.accountId}`
            )
            alertSent = true
          }
        }
        setMos(sample.mos)
      })
    }
    return () => {
      setMos(null)
    }
  }, [call.conf_status])

  useEffect(() => {
    createContactNumberMap(contacts)
  }, [contacts])

  const showContactSearch = () => {
    const input = addNumber
    if (!input.length) return false
    return !(
      (input.length && input.length === 10 && checkValidNumber(input)) ||
      addNumber.startsWith('team:')
    )
  }

  const parseDestination = () => {
    if (addNumber.startsWith('team:')) {
      const person = addNumber.split(':')[1].replaceAll(' ', '')
      if (person) {
        const usersList = Object.values(users)
        const dest: any = usersList.find((u: any) => {
          const n = `${u.firstName}${u.lastName}`.replaceAll(' ', '')
          return person === n
        })
        if (dest && dest.australisId) {
          return dest.australisId
        }
      }
    }
    return addNumber.replace(
      // eslint-disable-next-line no-useless-escape
      /[`~!@#$%^&*()_|+\-=?;:'",.<>\{\}\[\]\\\/\s+]/gi,
      ''
    )
  }

  const acceptCall = async () => {
    try {
      await device.audio.setInputDevice(inputDevice)
      // @ts-ignore
      if (device._activeCall) {
        enqueueSnackbar(
          'Switching Calls... Please wait while we connect you to your new call.',
          {
            anchorOrigin: {
              horizontal: 'right',
              vertical: 'bottom'
            },
            variant: 'info'
          }
        )
        const myDevice = getMyDeviceName(australisId)
        const activeConf = conferences.find(
          (conf) => !!conf.liveParticipants[myDevice]
        )
        if (activeConf) {
          await conferenceControl(
            auth0Token,
            accountId,
            australisId,
            activeConf.conferenceSid,
            {
              event: 'parkLeaveCall',
              conference: activeConf,
              conferenceSID: activeConf.conferenceSid,
              conferenceName: activeConf.name,
              participantSID: activeConf.liveParticipants[myDevice],
              participants: Object.values(activeConf.liveParticipants),
              targetParticipantSID: activeConf.liveParticipants[myDevice],
              accountId
            }
          )
        }
      }
      call.accept()
      removeIncomingCall(call.name)
    } catch (error) {
      enqueueSnackbar(
        `There was a problem connecting to the call. (Error: ${error})`,
        {
          anchorOrigin: {
            horizontal: 'right',
            vertical: 'bottom'
          },
          variant: 'error'
        }
      )
    }
  }

  const rejectCall = () => {
    console.log(call)
    call.reject()
    console.log(call.getStatus())
    removeIncomingCall(call.name)
  }

  const makeCall = async () => {
    try {
      const dest = parseDestination()
      await device.audio.setInputDevice(inputDevice)
      // @ts-ignore
      if (device._activeCall) {
        enqueueSnackbar(
          'Switching Calls... Please wait while we connect you to your new call.',
          {
            anchorOrigin: {
              horizontal: 'right',
              vertical: 'bottom'
            },
            variant: 'info'
          }
        )
        const myDevice = getMyDeviceName(australisId)
        const activeConf = conferences.find(
          (conf) => !!conf.liveParticipants[myDevice]
        )
        if (activeConf) {
          await conferenceControl(
            auth0Token,
            accountId,
            australisId,
            activeConf.conferenceSid,
            {
              event: 'parkLeaveCall',
              conference: activeConf,
              conferenceSID: activeConf.conferenceSid,
              conferenceName: activeConf.name,
              participantSID: activeConf.liveParticipants[myDevice],
              participants: Object.values(activeConf.liveParticipants),
              targetParticipantSID: activeConf.liveParticipants[myDevice],
              accountId
            }
          )
        }
      }
      if (dest.length > 14) {
        device.connect({
          params: { User: dest }
        })
      } else {
        console.log('Making call to', dest)
        device.connect({
          params: { To: parseDestination() }
        })
      }
      setDialing(true)
      await wait(8000)
      setDialing(false)
      setAddNumber('')
    } catch (error) {
      enqueueSnackbar(
        `There was a problem making the call. (Error: ${error})`,
        {
          anchorOrigin: {
            horizontal: 'right',
            vertical: 'bottom'
          },
          variant: 'error'
        }
      )
    }
  }

  const joinConference = async () => {
    try {
      await device.audio.setInputDevice(inputDevice)
      // @ts-ignore
      if (device._activeCall) {
        enqueueSnackbar(
          'Switching Calls... Please wait while we connect you to your new call.',
          {
            anchorOrigin: {
              horizontal: 'right',
              vertical: 'bottom'
            },
            variant: 'info'
          }
        )
        const myDevice = getMyDeviceName(australisId)
        const activeConf = conferences.find(
          (conf) => !!conf.liveParticipants[myDevice]
        )
        if (activeConf) {
          await conferenceControl(
            auth0Token,
            accountId,
            australisId,
            activeConf.conferenceSid,
            {
              event: 'parkLeaveCall',
              conference: activeConf,
              conferenceSID: activeConf.conferenceSid,
              conferenceName: activeConf.name,
              participantSID: activeConf.liveParticipants[myDevice],
              participants: Object.values(activeConf.liveParticipants),
              targetParticipantSID: activeConf.liveParticipants[myDevice],
              accountId
            }
          )
        }
      }
      device.connect({
        params: { Room: name }
      })
    } catch (error) {
      enqueueSnackbar(
        `There was a problem connecting to the call. (Error: ${error})`,
        {
          anchorOrigin: {
            horizontal: 'right',
            vertical: 'bottom'
          },
          variant: 'error'
        }
      )
    }
  }

  const leaveCall = () => {
    // @ts-ignore
    if (device && device._activeCall) {
      // @ts-ignore
      device._activeCall.disconnect()

      alertSent = false
      degradedCount = 0
    }
  }

  const sendDtmf = (key: string) => {
    // @ts-ignore
    if (device && device._activeCall) {
      // @ts-ignore
      device._activeCall.sendDigits(key)
    }
  }

  const muteSelf = () => {
    // @ts-ignore
    if (device && device._activeCall) {
      // @ts-ignore
      device._activeCall.mute(!device._activeCall.isMuted())
    }
  }

  const coldTransfer = () => {
    try {
      conferenceControl(auth0Token, accountId, australisId, conferenceSid, {
        event: 'coldTransfer',
        conference: call,
        conferenceSID: conferenceSid,
        conferenceName: call.name,
        participantSID: getAnyMyDeviceParticipantSid(
          liveParticipants,
          australisId,
          participants
        ),
        To: parseDestination(),
        accountId
      })
      enqueueSnackbar('Please wait while your transfer is initiated', {
        anchorOrigin: {
          horizontal: 'right',
          vertical: 'top'
        },
        variant: 'info'
      })
    } catch (err) {
      console.log(err)
    }
  }

  const warmTransfer = () => {
    // const otherParticipants = Object.entries(liveParticipants).filter(([label, sid]) => userIsParticipant(australisId, label))
    try {
      conferenceControl(auth0Token, accountId, australisId, conferenceSid, {
        event: 'warmTransfer',
        conference: call,
        conferenceSID: conferenceSid,
        conferenceName: call.name,
        participantSID: getAnyMyDeviceParticipantSid(
          liveParticipants,
          australisId,
          participants
        ),
        participants: Object.values(liveParticipants),
        To: parseDestination(),
        accountId
      })
      enqueueSnackbar('Please wait while your transfer is initiated', {
        anchorOrigin: {
          horizontal: 'right',
          vertical: 'top'
        },
        variant: 'info'
      })
    } catch (err) {
      console.log(err)
    }
  }

  const addParticipant = () => {
    try {
      conferenceControl(auth0Token, accountId, australisId, conferenceSid, {
        event: 'addToCall',
        conference: call,
        conferenceSID: conferenceSid,
        conferenceName: call.name,
        participantSID: getAnyMyDeviceParticipantSid(
          liveParticipants,
          australisId,
          participants
        ),
        participants: Object.values(liveParticipants),
        To: parseDestination(),
        accountId
      })
      enqueueSnackbar('Please wait while the invite is initiated', {
        anchorOrigin: {
          horizontal: 'right',
          vertical: 'top'
        },
        variant: 'info'
      })
    } catch (err) {
      console.log(err)
    }
  }

  const handleAddParticipantBlur = () => {
    if (!addNumber) {
      setAddOpen(false)
    }
  }

  const removeParticipant = (targetParticipantSID: string) => {
    try {
      conferenceControl(auth0Token, accountId, australisId, conferenceSid, {
        event: 'leaveCall',
        conference: call,
        conferenceSID: conferenceSid,
        conferenceName: call.name,
        participantSID: getAnyMyDeviceParticipantSid(
          liveParticipants,
          australisId,
          participants
        ),
        participants: Object.values(liveParticipants),
        targetParticipantSID,
        accountId
      })
    } catch (err) {
      console.log(err)
    }
  }

  const toggleHoldParticipant = (targetParticipantSID: string) => {
    try {
      conferenceControl(auth0Token, accountId, australisId, conferenceSid, {
        event: 'hold',
        conference: call,
        conferenceSID: conferenceSid,
        conferenceName: call.name,
        participantSID: getAnyMyDeviceParticipantSid(
          liveParticipants,
          australisId,
          participants
        ),
        participants: Object.values(liveParticipants),
        targetParticipantSID,
        hold: !call.participants[targetParticipantSID].hold,
        accountId
      })
    } catch (err) {
      console.log(err)
    }
  }

  const toggleMuteParticipant = (targetParticipantSID: string) => {
    try {
      conferenceControl(auth0Token, accountId, australisId, conferenceSid, {
        event: 'mute',
        conference: call,
        conferenceSID: conferenceSid,
        conferenceName: call.name,
        participantSID: getAnyMyDeviceParticipantSid(
          liveParticipants,
          australisId,
          participants
        ),
        participants: Object.values(liveParticipants),
        targetParticipantSID,
        mute: !call.participants[targetParticipantSID].muted,
        accountId
      })
    } catch (err) {
      console.log(err)
    }
  }

  const addDigit = (digit: string) => {
    setAddNumber(addNumber + digit)
  }

  const closeNotes = () => {
    setNotesOpen(false)
    setDialpadOpen(true)
  }

  const parts = liveParticipants ? Object.keys(liveParticipants) : []

  const renderContactSearch = () => {
    const filter = addNumber.toLowerCase()
    const filtered = filterContacts(contacts, filter, true)
    const usersList = []
    for (const u in users) {
      usersList.push(users[u])
    }
    const filteredUsers = filterUsers(usersList, filter)
    return (
      <Paper style={{ maxHeight: 180, width: '100%', overflowY: 'scroll' }}>
        <List style={{ height: '100%', width: '100%', padding: 0 }}>
          <div style={{ opacity: 0.7, padding: '0px 5px' }}>
            Showing team and contact results
          </div>
          <Divider />
          {filteredUsers.map((u, index) => {
            return (
              <ListItem
                key={u.australisId + 'phonesearch'}
                button
                onClick={() =>
                  setAddNumber(`team: ${u.firstName} ${u.lastName}`)
                }
                style={{
                  display: 'flex',
                  width: '100%',
                  justifyContent: 'space-between',
                  opacity: 0.9
                }}
              >
                <div
                  style={{
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    width: 'auto'
                  }}
                >
                  {`${u.firstName} ${u.lastName} -${
                    u.extension ? ` ext. ${u.extension}` : ''
                  } (Team)`}
                </div>
                <Divider component="li" />
              </ListItem>
            )
          })}
          {filtered.map((contact, index) => {
            if (index > 20) return null
            const { contactDetails } = contact
            return (
              <div key={contact.contactId}>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: 5
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      width: '100%',
                      justifyContent: 'space-between'
                    }}
                  >
                    <div
                      style={{
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        width: 'auto'
                      }}
                    >
                      {`${contactDetails.firstName} ${contactDetails.lastName}`}
                    </div>
                  </div>
                  {contactDetails.phoneNumbers.length
                    ? contactDetails.phoneNumbers.map((number: any) => {
                        return (
                          <ListItem
                            key={`${number.number}-${contact.contactId}-phone`}
                            button
                            onClick={() =>
                              setAddNumber(
                                number.number.substring(
                                  number.number.length - 10
                                )
                              )
                            }
                            style={{
                              display: 'flex',
                              width: '100%',
                              justifyContent: 'space-between',
                              opacity: 0.9
                            }}
                          >
                            <div
                              style={{
                                textOverflow: 'ellipsis',
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                width: 'auto'
                              }}
                            >
                              {number.number
                                ? `${formatNumber(number.number)} ${
                                    number.name ? ` - ${number.name}` : ''
                                  } ${
                                    number.number ===
                                    contactDetails.defaultPhoneNumber
                                      ? '(default)'
                                      : ''
                                  }`
                                : ''}
                            </div>
                          </ListItem>
                        )
                      })
                    : ''}
                </div>
                <Divider component="li" />
              </div>
            )
          })}
        </List>
      </Paper>
    )
  }

  if (call === 'new') {
    return (
      <>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between'
          }}
        >
          <TextField
            type="text"
            spellCheck={false}
            label="Type a number or name..."
            onChange={(e) => setAddNumber(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                makeCall()
              }
            }}
            value={addNumber}
            autoComplete="off"
            disabled={dialing}
            fullWidth
            autoFocus
            variant="filled"
            inputRef={newInputRef}
          />
          <IconButton
            sx={{
              position: 'absolute',
              right: 0,
              padding: '15px',
              color: 'green'
            }}
            disabled={!addNumber.length || dialing}
            onClick={() => makeCall()}
          >
            <CallIcon />
          </IconButton>
        </Box>
        <Popper
          style={{
            zIndex: 1301,
            width: newInputRef.current ? newInputRef.current.offsetWidth : 0
          }}
          placement="bottom"
          anchorEl={newInputRef.current}
          open={showContactSearch() && !dialing}
        >
          {renderContactSearch()}
        </Popper>
        <Box
          sx={{
            padding: '10px 0 0 0',
            overflow: 'hidden',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end'
          }}
          id={conferenceSid}
          {...other}
        >
          {!dialing ? (
            <>
              <Divider />
              {dialpadOpen ? (
                <CardContent>
                  <Dialpad sendDigits={addDigit} />
                </CardContent>
              ) : null}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 1
                }}
              >
                <IconButton
                  onClick={() => {
                    setDialpadOpen(!dialpadOpen)
                    setNotesOpen(!notesOpen)
                  }}
                  sx={{ backgroundColor: dialpadOpen ? 'lightgray' : 'none' }}
                >
                  <DialpadIcon fontSize="large" />
                </IconButton>
                <IconButton
                  sx={{ color: 'darkgreen' }}
                  onClick={() => makeCall()}
                  disabled={!addNumber.length}
                >
                  <CallIcon fontSize="large" />
                </IconButton>
              </Box>
            </>
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'column',
                justifyContent: 'center',
                padding: 1,
                height: '100%'
              }}
            >
              <img
                alt="Corvum"
                src={ravenImage}
                style={{ height: 75, backgroundColor: '#D2C8F3' }}
                className="dialing-animation"
              />
              <Typography variant="overline">Initiating Call...</Typography>
            </Box>
          )}
        </Box>
      </>
    )
  }

  if (call.incoming) {
    return (
      <>
        <Box
          sx={{
            padding: '10px 0 0 0',
            display: 'flex',
            height: '100%',
            flexDirection: 'column',
            justifyContent: 'space-between',
            overflow: 'hidden'
          }}
          id={conferenceSid}
          {...other}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}
          >
            <Scrollbar supressMobile>
              <List>
                {parts.map((p) => {
                  const details = participants[liveParticipants[p]]
                  let label = details.participantLabel
                  if (label.length === 12) {
                    const formatted = formatNumber(label)
                    const c = contactNumberMap[formatted]
                    if (c) {
                      const { contactDetails } = c
                      label = `${contactDetails.firstName} ${contactDetails.lastName}`
                    }
                  }
                  if (details.australisId) {
                    if (details.australisId === australisId) {
                      return null
                    }
                    const userDetails =
                      users[details.australisId.replaceAll('-', '')]
                    if (userDetails) {
                      label = `${userDetails.firstName} ${userDetails.lastName}`
                    }
                  }
                  return (
                    <ListItem button key={`${p}-selectedcall`}>
                      <ListItemAvatar>
                        <Avatar key={`${p}-${conferenceSid}-avatar2`} />
                      </ListItemAvatar>
                      <ListItemText sx={{ maxWidth: '110px' }}>
                        <Typography noWrap title={label}>
                          {label}
                        </Typography>
                      </ListItemText>
                    </ListItem>
                  )
                })}
              </List>
            </Scrollbar>
          </Box>
          <Box>
            <Divider />
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-evenly',
                padding: '6px',
                width: '100%'
              }}
            >
              <Tooltip title="Answer" placement="top">
                <IconButton
                  sx={{ color: 'green', border: 'solid green 2px' }}
                  onClick={() => acceptCall()}
                >
                  <CallIcon fontSize="large" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Reject" placement="top">
                <IconButton
                  sx={{ color: 'darkred', border: 'solid darkred 2px' }}
                  onClick={() => rejectCall()}
                >
                  <CallEndIcon fontSize="large" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>
      </>
    )
  }

  const active = Object.keys(liveParticipants).find((p) =>
    userIsParticipant(australisId, p)
  )

  return (
    <>
      {addOpen && (active || onOtherDevices) ? (
        <>
          <TextField
            type="text"
            spellCheck={false}
            label="Type a number or name..."
            onChange={(e) => setAddNumber(e.target.value)}
            onBlur={handleAddParticipantBlur}
            value={addNumber}
            autoComplete="off"
            fullWidth
            autoFocus
            variant="filled"
            inputRef={addInputRef}
          />
          <Popper
            style={{
              zIndex: 1301,
              width: addInputRef.current ? addInputRef.current.offsetWidth : 0
            }}
            placement="bottom"
            anchorEl={addInputRef.current}
            open={showContactSearch()}
          >
            {renderContactSearch()}
          </Popper>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Button
              sx={{ width: '33%', fontSize: '10px' }}
              onClick={() => warmTransfer()}
              disabled={!addNumber.length}
            >
              <PhoneForwardedIcon />
              Warm Transfer
            </Button>
            <Button
              sx={{ width: '33%', fontSize: '10px' }}
              onClick={() => coldTransfer()}
              disabled={!addNumber.length}
            >
              <PhoneForwardedIcon />
              Cold Transfer
            </Button>
            <Button
              sx={{ width: '33%', fontSize: '10px' }}
              onClick={() => addParticipant()}
              disabled={!addNumber.length}
            >
              <PersonAddIcon />
              Add Participant
            </Button>
          </Box>
        </>
      ) : null}

      <Typography sx={{ textAlign: 'center', pt: 2 }} variant="h6">
        Participants
        {mos ? (
          <Box sx={{ position: 'absolute', right: '10px', top: '15px' }}>
            {getMosIcon(mos)}
          </Box>
        ) : null}
      </Typography>

      <Box
        sx={{
          padding: '10px 0 0 0',
          display: 'flex',
          height: '100%',
          flexDirection: 'column',
          justifyContent: 'space-between',
          overflow: 'hidden'
        }}
        id={conferenceSid}
        {...other}
      >
        <Box
          sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
        >
          <ListItem
            disabled={!active && !onOtherDevices}
            button
            onClick={() => setAddOpen(!addOpen)}
            sx={{ backgroundColor: addOpen ? 'lightgray' : 'none' }}
          >
            <ListItemAvatar>
              <Avatar>
                <AddIcon />
              </Avatar>
            </ListItemAvatar>

            <ListItemText>
              <Typography noWrap>Add or transfer...</Typography>
            </ListItemText>
          </ListItem>

          <Scrollbar supressMobile>
            <List>
              {parts.map((p) => {
                const details = participants[liveParticipants[p]]
                let label = details.participantLabel
                if (label.length === 11 || label.length === 12) {
                  const formatted = formatNumber(label)
                  const c = contactNumberMap[formatted]
                  if (c) {
                    const { contactDetails } = c
                    label = `${contactDetails.firstName} ${contactDetails.lastName}`
                  }
                }
                if (details.australisId) {
                  if (details.australisId === australisId) {
                    return (
                      <ListItem button key={`${p}-selectedcall`}>
                        <ListItemAvatar>
                          <Avatar key={`${p}-${conferenceSid}-avatar2`} />
                        </ListItemAvatar>
                        <ListItemText sx={{ maxWidth: '110px' }}>
                          <Typography noWrap title="Me">
                            Me
                          </Typography>
                        </ListItemText>
                      </ListItem>
                    )
                  }
                  const userDetails =
                    users[details.australisId.replaceAll('-', '')]
                  if (userDetails) {
                    label = `${userDetails.firstName} ${userDetails.lastName}`
                  }
                }
                const deviceType = getDeviceTypeFromLabel(
                  details.participantLabel
                )
                return (
                  <ListItem button key={`${p}-selectedcall`}>
                    <ListItemAvatar>
                      {deviceType ? (
                        <Badge
                          color="primary"
                          overlap="circular"
                          badgeContent={deviceType}
                        >
                          <Avatar key={`${p}-${conferenceSid}-avatar2`} />
                        </Badge>
                      ) : (
                        <Avatar key={`${p}-${conferenceSid}-avatar2`} />
                      )}
                    </ListItemAvatar>
                    <ListItemText sx={{ maxWidth: '105px' }}>
                      <Typography noWrap title={label}>
                        {formatNumber(label)}
                      </Typography>
                    </ListItemText>
                    {(active || onOtherDevices) && (
                      <ListItemSecondaryAction>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center'
                          }}
                        >
                          {/* <IconButton
                          disabled={!active}
                          onClick={() =>
                            toggleMuteParticipant(liveParticipants[p])
                          }
                        >
                          {details.muted === true ? (
                            <MicOffIcon />
                          ) : (
                            <MicIcon />
                          )}
                        </IconButton> */}
                          <IconButton
                            disabled={!active && !onOtherDevices}
                            onClick={() =>
                              toggleHoldParticipant(liveParticipants[p])
                            }
                            sx={{
                              padding: '10px',
                              backgroundColor:
                                details.hold === true ? 'lightgray' : 'none'
                            }}
                          >
                            <PhonePausedIcon />
                          </IconButton>
                          <IconButton
                            disabled={!active && !onOtherDevices}
                            onClick={() =>
                              removeParticipant(liveParticipants[p])
                            }
                            sx={{
                              padding: '10px',
                              backgroundColor: 'none'
                            }}
                          >
                            <CloseIcon sx={{ color: 'darkred' }} />
                          </IconButton>
                        </Box>
                      </ListItemSecondaryAction>
                    )}
                  </ListItem>
                )
              })}
            </List>
          </Scrollbar>
        </Box>

        <Box>
          <Divider />

          {active ? (
            <>
              {/* Dialpad */}
              {dialpadOpen && !notesOpen ? (
                <CardContent>
                  <Dialpad sendDigits={sendDtmf} />
                </CardContent>
              ) : null}

              {/* Notes */}
              {notesOpen ? (
                <CardContent>
                  <Notes call={call} closeNotes={closeNotes} />
                </CardContent>
              ) : null}

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 1
                }}
              >
                <Tooltip title="Call Notes" placement="top">
                  <IconButton
                    onClick={() => {
                      setNotesOpen(!notesOpen)
                      setDialpadOpen(!dialpadOpen)
                    }}
                    sx={{ backgroundColor: notesOpen ? 'lightgray' : 'none' }}
                  >
                    <BorderColorIcon fontSize="large" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Open Keypad" placement="top">
                  <IconButton
                    onClick={() => {
                      setDialpadOpen(!dialpadOpen)
                      setNotesOpen(!notesOpen)
                    }}
                    sx={{ backgroundColor: dialpadOpen ? 'lightgray' : 'none' }}
                  >
                    <DialpadIcon fontSize="large" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Leave Call" placement="top">
                  <IconButton
                    sx={{ color: 'darkred' }}
                    onClick={() => leaveCall()}
                  >
                    <CallEndIcon fontSize="large" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Mute Myself" placement="top">
                  <IconButton onClick={() => muteSelf()}>
                    {device &&
                    // @ts-ignore
                    device._activeCall &&
                    // @ts-ignore
                    device._activeCall.isMuted() ? (
                      <MicOffIcon fontSize="large" />
                    ) : (
                      <MicIcon fontSize="large" />
                    )}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Mute Audio" placement="top">
                  <IconButton>
                    {call.volumeOff ? (
                      <VolumeOffIcon fontSize="large" />
                    ) : (
                      <VolumeUpIcon fontSize="large" />
                    )}
                  </IconButton>
                </Tooltip>
              </Box>
            </>
          ) : (
            <>
              {notesOpen ? (
                <CardContent>
                  <Notes call={call} closeNotes={closeNotes} />
                </CardContent>
              ) : null}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent:
                    liveNotes[call.conferenceSid] || onOtherDevices
                      ? 'space-evenly'
                      : 'center',
                  padding: '6px'
                }}
              >
                <Tooltip title="Join Call" placement="top">
                  <IconButton
                    sx={{ color: 'green', border: 'solid green 2px' }}
                    onClick={() => joinConference()}
                  >
                    <CallIcon fontSize="large" />
                  </IconButton>
                </Tooltip>
                {liveNotes[call.conferenceSid] || onOtherDevices ? (
                  <Tooltip title="Call Notes" placement="top">
                    <IconButton
                      onClick={() => {
                        setNotesOpen(!notesOpen)
                        setDialpadOpen(!dialpadOpen)
                      }}
                    >
                      <BorderColorIcon fontSize="large" />
                    </IconButton>
                  </Tooltip>
                ) : null}
              </Box>
            </>
          )}
        </Box>
      </Box>
    </>
  )
}

SelectedCall.propTypes = {
  call: PropTypes.any,
  contacts: PropTypes.any,
  device: PropTypes.any,
  users: PropTypes.any,
  inputDevice: PropTypes.string,
  removeIncomingCall: PropTypes.func
}

export default SelectedCall
